import apiClient, { API_ENDPOINTS, rejectWithError } from "api";
import {
  buildCapturedContentFormData,
  buildIotContentFormData,
  buildIotContentPartFormData,
  buildPreSignedUrlRequest,
  buildProcessVideoFormData
} from "domains/iot/services/iotRequestBuilders";

const { fileStore, iot } = API_ENDPOINTS;

const rejectIotError = (error, label) => rejectWithError(error, label);

export const uploadCapturedContent = async (file, avpsIotRequest) => {
  try {
    return await apiClient.upload(
      iot.uploadCapturedContent,
      buildCapturedContentFormData(file, avpsIotRequest)
    );
  } catch (error) {
    return rejectIotError(error, "Error uploading captured content");
  }
};

export const detectUser = async (avpsIotRecognitionRequest) => {
  try {
    return await apiClient.post(iot.detectUser, avpsIotRecognitionRequest);
  } catch (error) {
    return rejectIotError(error, "Error detecting user");
  }
};

export const uploadContent = async (file, companyCode, type, primaryId) => {
  try {
    return await apiClient.upload(
      iot.uploadContent,
      buildIotContentFormData(file, companyCode, type, primaryId)
    );
  } catch (error) {
    return rejectIotError(error, "Error uploading content");
  }
};

export const uploadContentInParts = async (videoChunk, primaryId) => {
  try {
    return await apiClient.upload(
      iot.uploadContentInParts,
      buildIotContentPartFormData(videoChunk, primaryId)
    );
  } catch (error) {
    return rejectIotError(error, "Error uploading content in parts");
  }
};

export const processVideo = async (companyCode, type, primaryId) => {
  try {
    return await apiClient.upload(
      iot.processVideo,
      buildProcessVideoFormData(companyCode, type, primaryId)
    );
  } catch (error) {
    return rejectIotError(error, "Error processing video");
  }
};

export const getPreSignedURL = async (key) => {
  try {
    return await apiClient.post(
      fileStore.preSignedUrl,
      buildPreSignedUrlRequest(key),
      { responseType: "text" }
    );
  } catch (error) {
    return rejectIotError(error, "Error generating pre-signed URL");
  }
};

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

/**
 * Normalize a pre-signed URL response into a plain, usable URL string. The
 * backend may return either a bare URL (text/plain) or a JSON-encoded string /
 * object (`{ url | displayLink | preSignedUrl | data }`).
 */
const normalizeSignedUrl = (value) => {
  const text = String(value == null ? "" : value).trim();
  if (!text) return "";
  if (text[0] === '"' || text[0] === "{") {
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === "string") return parsed.trim();
      if (parsed && typeof parsed === "object") {
        return String(parsed.url || parsed.displayLink || parsed.preSignedUrl || parsed.data || "").trim();
      }
    } catch (e) {
      // Not JSON — fall through and use the raw text.
    }
  }
  return text;
};

/**
 * Resolve a DURABLE stored key/URL into a fresh display (pre-signed) URL, used
 * at render time by the canvas reader/preview so a pre-signed URL is NEVER
 * persisted. Absolute http(s) URLs are returned unchanged (no backend call);
 * on any failure the stored key is returned so rendering never blanks out.
 *
 * @param {string} key durable stored key or already-usable URL
 * @returns {Promise<string>} a display URL
 */
export const resolvePresignedUrl = async (key) => {
  const storedKey = String(key || "").trim();
  if (!storedKey) return "";
  if (isHttpUrl(storedKey)) return storedKey;
  try {
    const response = await getPreSignedURL(storedKey);
    return normalizeSignedUrl(response) || storedKey;
  } catch (error) {
    return storedKey;
  }
};
